import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";

export default function BranchPmcApproval() {
  const [pmcs, setPmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [branchState, setBranchState] = useState("Gujarat");
  const [busyId, setBusyId] = useState("");
  const [editingPmc, setEditingPmc] = useState(null);
  const [editForm, setEditForm] = useState({
    organizationName: "",
    name: "",
    designation: "",
    email: "",
    mobile: "",
    district: "",
    address: "",
    registrationNumber: "",
  });

  const isDeleted = (p = {}) =>
    p.isDeleted === true ||
    p.deleted === true ||
    Boolean(p.deletedAt) ||
    ["DELETED", "ARCHIVED", "REMOVED"].includes(
      String(p.status || "").toUpperCase(),
    );

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      const d = value?.toDate ? value.toDate() : new Date(value);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const getStatus = (p) => {
    const value = String(
      p.branchApprovalStatus ||
        p.roleApprovalStatus ||
        p.status ||
        "PENDING",
    ).toUpperCase();
    if (value.includes("APPROVED")) return "APPROVED";
    if (value.includes("REJECT")) return "REJECTED";
    if (value.includes("QUERY")) return "QUERY";
    return "PENDING";
  };

  const loadBranchProfile = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error("Please login as NAFED State Branch.");

    const snap = await getDoc(doc(db, "nafed_users", user.uid));
    if (!snap.exists()) throw new Error("State Branch profile not found.");
    const profile = snap.data();
    if (
      !["STATE_BRANCH", "NAFED_STATE_BRANCH", "BRANCH_OFFICE"].includes(
        String(profile.role || "").toUpperCase(),
      )
    )
      throw new Error("Only State Branch can manage PMC agencies.");
    if (!profile.state) throw new Error("State is missing in Branch profile.");
    setBranchState(profile.state);
    return profile;
  };

  const loadPmcs = async () => {
    try {
      setLoading(true);

      const branchProfile = await loadBranchProfile();
      const authorisedState = branchProfile.state;

      const q = query(
        collection(db, "nafed_users"),
        where("role", "==", "PMC"),
      );

      const snap = await getDocs(q);

      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(
          (p) =>
            (p.state || "").toLowerCase() === authorisedState.toLowerCase() &&
            !isDeleted(p),
        )
        .sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
        );

      setPmcs(data);
    } catch (e) {
      console.error("PMC loading failed:", e);
      alert("PMC loading failed. Please check Firestore rules.");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (pmc) => {
    setEditingPmc(pmc);
    setEditForm({
      organizationName:
        pmc.organizationName || pmc.pmcName || pmc.agencyName || "",
      name: pmc.name || pmc.contactPerson || "",
      designation: pmc.designation || "",
      email: pmc.email || "",
      mobile: pmc.mobile || "",
      district: pmc.district || "",
      address: pmc.address || pmc.fullAddress || "",
      registrationNumber: pmc.registrationNumber || pmc.registrationId || "",
    });
  };

  const saveEdit = async () => {
    if (!editingPmc) return;
    if (!editForm.organizationName.trim())
      return alert("PMC / Agency name is required.");
    if (!editForm.email.trim()) return alert("PMC email is required.");
    if (!window.confirm("Save changes to this PMC agency profile?")) return;

    try {
      setBusyId(editingPmc.id);
      const currentUser = auth.currentUser;
      const branchProfile = await loadBranchProfile();
      if (
        String(editingPmc.state || "").toLowerCase() !==
        String(branchProfile.state || "").toLowerCase()
      ) {
        throw new Error("This PMC belongs to another State Branch.");
      }
      await updateDoc(doc(db, "nafed_users", editingPmc.id), {
        ...editForm,
        contactPerson: editForm.name,
        fullAddress: editForm.address,
        editedByUid: currentUser?.uid || "",
        editedByEmail: currentUser?.email || "",
        editedByRole: "STATE_BRANCH",
        editedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "nafed_activity"), {
        action: "STATE_BRANCH_PMC_PROFILE_UPDATED",
        performedByUid: currentUser?.uid || "",
        performedByRole: "STATE_BRANCH",
        targetUid: editingPmc.id,
        targetRole: "PMC",
        state: branchState,
        changes: editForm,
        createdAt: serverTimestamp(),
      });
      setEditingPmc(null);
      await loadPmcs();
      alert("PMC profile updated successfully.");
    } catch (e) {
      console.error("PMC edit failed:", e);
      alert(e.message || "PMC profile update failed.");
    } finally {
      setBusyId("");
    }
  };

  const deletePmc = async (pmc) => {
    const name = pmc.organizationName || pmc.name || "PMC Agency";
    const reason = window.prompt(
      `Archive/Delete ${name}? Enter official reason:`,
      "Removed by NAFED State Branch",
    );
    if (!reason?.trim()) return;
    if (
      !window.confirm(
        `${name} will disappear from approval and allocation lists. Historical project records will remain for audit. Continue?`,
      )
    )
      return;

    try {
      setBusyId(pmc.id);
      const currentUser = auth.currentUser;
      const branchProfile = await loadBranchProfile();
      if (
        String(pmc.state || "").toLowerCase() !==
        String(branchProfile.state || "").toLowerCase()
      ) {
        throw new Error("This PMC belongs to another State Branch.");
      }
      await updateDoc(doc(db, "nafed_users", pmc.id), {
        isDeleted: true,
        deleted: true,
        status: "DELETED",
        branchApprovalStatus: "DELETED",
        roleApprovalStatus: "DELETED",
        registrationStatus: "DELETED",
        isActive: false,
        deletionReason: reason.trim(),
        deletedAt: serverTimestamp(),
        deletedByUid: currentUser?.uid || "",
        deletedByEmail: currentUser?.email || "",
        deletedByRole: "STATE_BRANCH",
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "nafed_activity"), {
        action: "STATE_BRANCH_PMC_ARCHIVED",
        performedByUid: currentUser?.uid || "",
        performedByRole: "STATE_BRANCH",
        targetUid: pmc.id,
        targetRole: "PMC",
        targetName: name,
        state: branchState,
        reason: reason.trim(),
        createdAt: serverTimestamp(),
      });
      await addDoc(collection(db, "nafed_mail_notifications"), {
        type: "PMC_ARCHIVED_BY_STATE_BRANCH",
        to: pmc.email || "",
        recipientUid: pmc.uid || pmc.id,
        subject: "PMC Portal Access Archived by NAFED State Branch",
        message: `Your PMC profile has been archived. Reason: ${reason.trim()}`,
        status: "PENDING",
        relatedCollection: "nafed_users",
        relatedId: pmc.id,
        createdAt: serverTimestamp(),
      });
      setPmcs((old) => old.filter((item) => item.id !== pmc.id));
      alert("PMC archived and removed from operational lists.");
    } catch (e) {
      console.error("PMC delete failed:", e);
      alert(e.message || "PMC archive/delete failed.");
    } finally {
      setBusyId("");
    }
  };

  useEffect(() => {
    loadPmcs();
  }, []);

  const updateStatus = async (pmc, actionStatus) => {
    const needsReason = ["REJECTED", "QUERY"].includes(actionStatus);
    const remark = needsReason
      ? window.prompt(
          actionStatus === "QUERY"
            ? "Enter required correction / document query:"
            : "Enter complete rejection reason:",
          "",
        )
      : "Approved by State Branch";
    if (needsReason && !remark?.trim()) return;

    if (
      !window.confirm(
        actionStatus === "APPROVED"
          ? "Approve this PMC Agency?"
          : actionStatus === "QUERY"
            ? "Raise this query and return the PMC file for correction?"
            : "Reject this PMC Agency registration?",
      )
    ) {
      return;
    }

    try {
      setBusyId(pmc.id);
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Session expired. Login again.");
      const branchProfile = await loadBranchProfile();
      if (
        String(pmc.state || "").toLowerCase() !==
        String(branchProfile.state || "").toLowerCase()
      ) {
        throw new Error("This PMC belongs to another State Branch.");
      }
      const finalStatus =
        actionStatus === "APPROVED"
          ? "APPROVED"
          : actionStatus === "QUERY"
            ? "QUERY_RAISED"
            : "REJECTED";
      const cleanRemark = String(remark || "").trim();
      const batch = writeBatch(db);
      batch.update(doc(db, "nafed_users", pmc.id), {
        status: finalStatus,
        branchApprovalStatus: actionStatus,
        roleApprovalStatus: actionStatus,
        branchApprovalRemark: cleanRemark,
        roleApprovalRemark: cleanRemark,
        branchApprovalAt: serverTimestamp(),
        roleApprovedAt:
          actionStatus === "APPROVED"
            ? serverTimestamp()
            : pmc.roleApprovedAt || null,
        approvedByUid: currentUser?.uid || "",
        approvedByEmail: currentUser?.email || "",
        approvedByName:
          branchProfile.name ||
          branchProfile.officerName ||
          branchProfile.organizationName ||
          "NAFED State Branch",
        approvedByRole: "NAFED_STATE_BRANCH",
        isActive: actionStatus === "APPROVED",
        updatedAt: serverTimestamp(),
      });
      batch.set(doc(collection(db, "nafed_mail_notifications")), {
        type: "PMC_BRANCH_APPROVAL_STATUS",
        to: pmc.email || "",
        subject:
          actionStatus === "APPROVED"
            ? "PMC Registration Approved by NAFED State Branch"
            : actionStatus === "QUERY"
              ? "Correction Required in PMC Registration"
              : "PMC Registration Rejected by NAFED State Branch",
        message:
          actionStatus === "APPROVED"
            ? `Dear ${pmc.name || pmc.organizationName || "PMC"}, your PMC registration has been approved by State Branch.`
            : actionStatus === "QUERY"
              ? `Dear ${pmc.name || pmc.organizationName || "PMC"}, correction is required in your registration. Query: ${cleanRemark}`
              : `Dear ${pmc.name || pmc.organizationName || "PMC"}, your registration has been rejected. Reason: ${cleanRemark}`,
        status: "PENDING",
        relatedCollection: "nafed_users",
        relatedId: pmc.id,
        createdAt: serverTimestamp(),
      });
      batch.set(doc(collection(db, "nafed_activity")), {
        action:
          actionStatus === "APPROVED"
            ? "STATE_BRANCH_PMC_APPROVED"
            : actionStatus === "QUERY"
              ? "STATE_BRANCH_PMC_QUERY_RAISED"
              : "STATE_BRANCH_PMC_REJECTED",
        targetUid: pmc.id,
        targetRole: "PMC",
        targetName: pmc.organizationName || pmc.name || "PMC Agency",
        state: branchProfile.state,
        remarks: cleanRemark,
        performedByUid: currentUser.uid,
        performedByEmail: currentUser.email || "",
        performedByRole: "STATE_BRANCH",
        createdAt: serverTimestamp(),
      });
      await batch.commit();
      alert(
        actionStatus === "QUERY"
          ? "PMC query raised successfully."
          : `PMC ${actionStatus.toLowerCase()} successfully.`,
      );
      await loadPmcs();
    } catch (e) {
      console.error("PMC status update failed:", e);
      alert(e.message || "PMC status update failed.");
    } finally {
      setBusyId("");
    }
  };

  const filtered = useMemo(() => {
    return pmcs.filter((p) => {
      const status = getStatus(p);
      const text =
        `${p.organizationName || ""} ${p.name || ""} ${p.email || ""} ${p.mobile || ""} ${p.state || ""} ${p.district || ""}`.toLowerCase();

      return (
        (filter === "ALL" || status === filter) &&
        text.includes(search.toLowerCase())
      );
    });
  }, [pmcs, filter, search]);

  const counts = {
    total: pmcs.length,
    pending: pmcs.filter((p) => getStatus(p) === "PENDING").length,
    query: pmcs.filter((p) => getStatus(p) === "QUERY").length,
    approved: pmcs.filter((p) => getStatus(p) === "APPROVED").length,
    rejected: pmcs.filter((p) => getStatus(p) === "REJECTED").length,
  };

  return (
    <>
      <style>{`
        .pmc-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .pmc-hero {
          background: white;
          border-radius: 18px;
          padding: 24px;
          border: 1px solid #cbd5e1;
          box-shadow: 0 8px 24px rgba(15,23,42,.06);
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .pmc-hero h1 {
          margin: 0;
          font-size: 26px;
          font-weight: 950;
          color: #071f45;
        }

        .pmc-hero p {
          margin: 7px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .pmc-btn {
          border: 0;
          background: #062b55;
          color: white;
          border-radius: 10px;
          padding: 11px 16px;
          font-weight: 900;
          cursor: pointer;
        }

        .pmc-kpis {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 14px;
        }

        .pmc-kpi {
          background: white;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #cbd5e1;
          box-shadow: 0 8px 24px rgba(15,23,42,.06);
        }

        .pmc-kpi label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .12em;
          color: #64748b;
          font-weight: 950;
        }

        .pmc-kpi b {
          display: block;
          margin-top: 8px;
          font-size: 28px;
          font-weight: 950;
          color: #071f45;
        }

        .filter-bar {
          background: white;
          border-radius: 14px;
          padding: 14px;
          border: 1px solid #cbd5e1;
          display: grid;
          grid-template-columns: 1fr auto auto auto auto auto;
          gap: 10px;
          align-items: center;
        }

        .search-input {
          height: 42px;
          border: 1px solid #64748b;
          border-radius: 10px;
          padding: 0 12px;
          font-weight: 800;
          outline: none;
        }

        .filter-btn {
          border: 1px solid #64748b;
          background: #f8fafc;
          color: #071f45;
          border-radius: 999px;
          padding: 9px 14px;
          font-weight: 900;
          cursor: pointer;
        }

        .filter-btn.active {
          background: #062b55;
          color: white;
          border-color: #062b55;
        }

        .pmc-panel {
          background: white;
          border-radius: 16px;
          border: 1px solid #111827;
          box-shadow: 0 8px 24px rgba(15,23,42,.06);
          overflow: hidden;
        }

        .panel-head {
          padding: 16px 18px;
          border-bottom: 2px solid #111827;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8fafc;
        }

        .panel-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 18px;
          font-weight: 950;
        }

        .panel-head span {
          font-size: 12px;
          color: #475569;
          font-weight: 900;
        }

        .table-wrap {
          overflow-x: auto;
        }

        .pmc-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 1350px;
          font-size: 13px;
          border: 2px solid #111827;
        }

        .pmc-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 13px 14px;
          border: 1.5px solid #111827;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
        }

        .pmc-table td {
          padding: 14px;
          border: 1.5px solid #111827;
          font-weight: 750;
          vertical-align: top;
          color: #0f172a;
          background: white;
        }

        .pmc-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .pmc-table tbody tr:hover td {
          background: #eef6ff;
        }

        .org-name {
          font-weight: 950;
          color: #071f45;
        }

        .muted {
          color: #64748b;
          font-size: 12px;
          line-height: 1.6;
          font-weight: 750;
        }

        .status {
          border-radius: 999px;
          padding: 7px 11px;
          font-size: 11px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
          border: 1px solid transparent;
        }

        .pending {
          background: #fef3c7;
          color: #92400e;
          border-color: #f59e0b;
        }

        .approved {
          background: #dcfce7;
          color: #166534;
          border-color: #16a34a;
        }

        .rejected {
          background: #fee2e2;
          color: #991b1b;
          border-color: #ef4444;
        }

        .query {
          background: #ffedd5;
          color: #9a3412;
          border-color: #fb923c;
        }

        .action-box {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
        }

        .action {
          border: 0;
          border-radius: 10px;
          padding: 9px 12px;
          font-weight: 900;
          cursor: pointer;
          font-size: 12px;
        }

        .approve {
          background: #16a34a;
          color: white;
        }

        .reject {
          background: #ef4444;
          color: white;
        }

        .action.query {
          background: #d97706;
          color: white;
        }

        .edit {
          background: #1f5fa9;
          color: white;
        }

        .delete {
          background: #9f261e;
          color: white;
        }

        .edit-modal {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: grid;
          place-items: center;
          padding: 20px;
          background: rgba(2, 18, 39, .72);
          backdrop-filter: blur(5px);
        }

        .edit-window {
          width: min(760px, 96vw);
          max-height: 92vh;
          overflow: auto;
          background: white;
          border: 1px solid #9caaba;
          border-radius: 14px;
          box-shadow: 0 30px 90px rgba(0, 0, 0, .4);
        }

        .edit-head {
          padding: 17px 20px;
          background: linear-gradient(105deg, #0b315e, #176341);
          border-bottom: 4px solid #d8a23b;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .edit-head h3 { margin: 0; font-size: 19px; }
        .edit-head button { border: 1px solid white; border-radius: 6px; background: white; color: #12345f; padding: 8px 11px; font-weight: 900; }
        .edit-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 13px; padding: 20px; }
        .edit-field { display: grid; gap: 6px; }
        .edit-field.full { grid-column: 1 / -1; }
        .edit-field label { font-size: 10px; color: #42566d; font-weight: 900; text-transform: uppercase; letter-spacing: .06em; }
        .edit-field input, .edit-field textarea { width: 100%; border: 1px solid #748295; border-radius: 6px; padding: 10px 11px; font: inherit; }
        .edit-field textarea { min-height: 85px; resize: vertical; }
        .edit-foot { display: flex; justify-content: flex-end; gap: 9px; padding: 14px 20px; background: #f2f5f8; border-top: 1px solid #bac4cf; }

        .disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .empty {
          text-align: center;
          padding: 35px;
          font-weight: 900;
          color: #64748b;
        }

        @media(max-width: 900px) {
          .pmc-kpis {
            grid-template-columns: repeat(2, 1fr);
          }

          .filter-bar {
            grid-template-columns: 1fr;
          }

          .pmc-hero {
            flex-direction: column;
            align-items: flex-start;
          }

          .edit-grid { grid-template-columns: 1fr; }
          .edit-field.full { grid-column: auto; }
        }
      `}</style>

      <div className="pmc-page">
        <section className="pmc-hero">
          <div>
            <h1>PMC Approval</h1>
            <p>
              State Branch approves PMC agencies. Approved PMCs can be allocated
              districts and can process NAFED PACS projects.
            </p>
            <p className="muted">Current Branch State: {branchState}</p>
          </div>

          <button className="pmc-btn" onClick={loadPmcs}>
            Refresh
          </button>
        </section>

        <section className="pmc-kpis">
          <div className="pmc-kpi">
            <label>Total PMCs</label>
            <b>{counts.total}</b>
          </div>
          <div className="pmc-kpi">
            <label>Pending</label>
            <b>{counts.pending}</b>
          </div>
          <div className="pmc-kpi">
            <label>Query Raised</label>
            <b>{counts.query}</b>
          </div>
          <div className="pmc-kpi">
            <label>Approved</label>
            <b>{counts.approved}</b>
          </div>
          <div className="pmc-kpi">
            <label>Rejected</label>
            <b>{counts.rejected}</b>
          </div>
        </section>

        <section className="filter-bar">
          <input
            className="search-input"
            placeholder="Search PMC, contact, email, mobile, district..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {[
            ["ALL", "All"],
            ["PENDING", "Pending"],
            ["QUERY", "Query"],
            ["APPROVED", "Approved"],
            ["REJECTED", "Rejected"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={`filter-btn ${filter === value ? "active" : ""}`}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </section>

        <section className="pmc-panel">
          <div className="panel-head">
            <h3>PMC Registration Requests</h3>
            <span>
              Showing {filtered.length} of {pmcs.length}
            </span>
          </div>

          {loading ? (
            <div className="empty">Loading PMCs...</div>
          ) : (
            <div className="table-wrap">
              <table className="pmc-table">
                <thead>
                  <tr>
                    <th>Sr</th>
                    <th>PMC / Agency</th>
                    <th>State / District</th>
                    <th>Contact Person</th>
                    <th>Email / Mobile</th>
                    <th>Registration Date</th>
                    <th>Status</th>
                    <th>Approved / Rejected By</th>
                    <th>Approval Date</th>
                    <th>Remark</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((p, index) => {
                    const status = getStatus(p);
                    const isFinal =
                      status === "APPROVED" || status === "REJECTED";

                    return (
                      <tr key={p.id}>
                        <td>{index + 1}</td>

                        <td>
                          <div className="org-name">
                            {p.organizationName ||
                              p.pmcName ||
                              p.agencyName ||
                              "-"}
                          </div>
                          <div className="muted">User ID: {p.uid || p.id}</div>
                          <div className="muted">Role: PMC</div>
                        </td>

                        <td>
                          <b>{p.state || "-"}</b>
                          <div className="muted">
                            District: {p.district || "-"}
                          </div>
                        </td>

                        <td>
                          <b>{p.name || p.contactPerson || "-"}</b>
                          <div className="muted">
                            {p.designation || "PMC Admin"}
                          </div>
                        </td>

                        <td>
                          <b>{p.email || "-"}</b>
                          <div className="muted">{p.mobile || "-"}</div>
                        </td>

                        <td>{formatDateTime(p.createdAt)}</td>

                        <td>
                          <span
                            className={`status ${
                              status === "APPROVED"
                                ? "approved"
                                : status === "REJECTED"
                                  ? "rejected"
                                  : status === "QUERY"
                                    ? "query"
                                    : "pending"
                            }`}
                          >
                            {status}
                          </span>
                          <div className="muted">
                            Firestore: {p.status || "-"}
                          </div>
                        </td>

                        <td>
                          <b>{p.approvedByName || "-"}</b>
                          <div className="muted">
                            {p.approvedByEmail || "-"}
                          </div>
                          <div className="muted">{p.approvedByRole || "-"}</div>
                        </td>

                        <td>
                          {formatDateTime(
                            p.branchApprovalAt || p.roleApprovedAt,
                          )}
                        </td>

                        <td>
                          {p.branchApprovalRemark ||
                            p.roleApprovalRemark ||
                            "-"}
                        </td>

                        <td>
                          <div className="action-box">
                            <button
                              className={`action approve ${
                                isFinal ? "disabled" : ""
                              }`}
                              disabled={isFinal || busyId === p.id}
                              onClick={() => updateStatus(p, "APPROVED")}
                            >
                              Approve
                            </button>

                            <button
                              className="action query"
                              disabled={isFinal || busyId === p.id}
                              onClick={() => updateStatus(p, "QUERY")}
                            >
                              Raise Query
                            </button>

                            <button
                              className={`action reject ${
                                isFinal ? "disabled" : ""
                              }`}
                              disabled={isFinal || busyId === p.id}
                              onClick={() => updateStatus(p, "REJECTED")}
                            >
                              Reject
                            </button>

                            <button
                              className="action edit"
                              disabled={busyId === p.id}
                              onClick={() => startEdit(p)}
                            >
                              Edit
                            </button>

                            <button
                              className="action delete"
                              disabled={busyId === p.id}
                              onClick={() => deletePmc(p)}
                            >
                              {busyId === p.id ? "Working..." : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!filtered.length && (
                    <tr>
                      <td colSpan="11" className="empty">
                        No PMC registration found in {branchState}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {editingPmc && (
        <div className="edit-modal" role="dialog" aria-modal="true">
          <section className="edit-window">
            <div className="edit-head">
              <div>
                <h3>Edit PMC Agency</h3>
                <small>State Branch authorised profile correction</small>
              </div>
              <button onClick={() => setEditingPmc(null)}>× Close</button>
            </div>
            <div className="edit-grid">
              {[
                ["organizationName", "PMC / Agency Name", "text"],
                ["registrationNumber", "Registration Number", "text"],
                ["name", "Contact Person", "text"],
                ["designation", "Designation", "text"],
                ["email", "Official Email", "email"],
                ["mobile", "Mobile Number", "tel"],
                ["district", "District", "text"],
              ].map(([key, label, type]) => (
                <label className="edit-field" key={key}>
                  <span>{label}</span>
                  <input
                    type={type}
                    value={editForm[key]}
                    onChange={(event) =>
                      setEditForm((old) => ({
                        ...old,
                        [key]: event.target.value,
                      }))
                    }
                  />
                </label>
              ))}
              <label className="edit-field full">
                <span>Office Address</span>
                <textarea
                  value={editForm.address}
                  onChange={(event) =>
                    setEditForm((old) => ({
                      ...old,
                      address: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="edit-foot">
              <button className="pmc-btn" onClick={() => setEditingPmc(null)}>
                Cancel
              </button>
              <button
                className="pmc-btn"
                disabled={busyId === editingPmc.id}
                onClick={saveEdit}
              >
                {busyId === editingPmc.id ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
